import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Trophy, TrendingUp, TrendingDown, Activity, Target, BarChart3, RefreshCw, Download, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Map Active Stock company names to ASX ticker symbols
const NAME_TO_TICKER = {
  "4DMEDICAL": "4DX", "ALCOA": "AAI", "AMPOL": "ALD", "ARISTOCRAT": "ALL",
  "BENDIGO": "BEN", "BLUESCOPE": "BSL", "BRAMBLES": "BXB",
  "COCHLEAR": "COH", "COLES": "COL", "COMMONWEALTH": "CBA", "COMPUTERSHARE": "CPU",
  "DRONESHIELD": "DRO", "ELECTRO": "EOS", "EVOLUTION": "EVN", "FORTESCUE": "FMG",
  "GENESIS": "GMD", "GOODMAN": "GMG", "GREATLAND": "GML", "INSIGNIA": "IFL",
  "INSURANCE": "IAG", "JAMES": "JHX", "JB": "JBH", "LIGHT": "LNW", "LIONTOWN": "LTR",
  "LYNAS": "LYC", "MACQUARIE": "MQG", "METCASH": "MTS", "MINERAL": "MIN",
  "MIRVAC": "MGR", "NATIONAL": "NAB", "NETWEALTH": "NWL", "NEWMONT": "NEM",
  "NEXTDC": "NXT", "NORTHERN": "NST", "ORIGIN": "ORG", "PALADIN": "PDN",
  "PERSEUS": "PRU", "PLS": "PLS", "QANTAS": "QAN", "QUBE": "QUB",
  "RAMELIUS": "RMS", "RESMED": "RMD", "SANDFIRE": "SFR", "SANTOS": "STO",
  "SCENTRE": "SCG", "SIGMA": "SIG", "SIMS": "SGM", "SONIC": "SHL",
  "SOUTH32": "S32", "STOCKLAND": "SGP", "SUNCORP": "SUN", "TECHNOLOGY": "TNE",
  "TELIX": "TLX", "TELSTRA": "TLS", "TRANSURBAN": "TCL", "TREASURY": "TWE",
  "VIVA": "VEA", "WASHINGTON": "SOL", "WESFARMERS": "WES", "WESTGOLD": "WGX",
  "WESTPAC": "WBC", "WHITEHAVEN": "WHC", "WISETECH": "WTC", "WOODSIDE": "WDS",
  "WOOLWORTHS": "WOW", "XERO": "XRO", "YANCOAL": "YAL",
  "ANZ": "ANZ", "ASX": "ASX", "BHP": "BHP", "CSL": "CSL",
};

function SmartRankingsPage() {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weights, setWeights] = useState({
    activity: 0.2,
    value: 0.4,
    analyst: 0.4
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState('');
  
  // Store raw data so we can recalculate without re-fetching
  const rawData = useRef({ topPicksMap: null, activeMap: null, highPotentialMap: null });

  // Fetch data once on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Recalculate rankings when weights change (no re-fetch)
  useEffect(() => {
    if (rawData.current.topPicksMap) {
      calculateRankings(rawData.current.topPicksMap, rawData.current.activeMap, rawData.current.highPotentialMap, weights);
    }
  }, [weights]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [topPicksRes, activeRes, highPotentialRes] = await Promise.all([
        fetch(`${API_URL}/api/recommendations/latest?limit=500`),
        fetch(`${API_URL}/api/active/latest?limit=500`),
        fetch(`${API_URL}/api/52week-lows/latest?limit=500`)
      ]);

      const topPicks = await topPicksRes.json();
      const active = await activeRes.json();
      const highPotential = await highPotentialRes.json();

      const topPicksMap = new Map();
      const activeMap = new Map();
      const highPotentialMap = new Map();

      // Process Top Picks (Analyst Score)
      if (topPicks.success && topPicks.data) {
        const maxScore = Math.max(...topPicks.data.map(s => s.score || 0));
        topPicks.data.forEach((stock, index) => {
          topPicksMap.set(stock.symbol, {
            ...stock,
            analystRank: index + 1,
            analystScore: maxScore > 0 ? ((stock.score || 0) / maxScore) * 100 : 0,
            growthPotential: stock.potential_growth_pct || 0
          });
        });
      }

      // Process Active Stocks — map company names to ticker symbols
      if (active.success && active.data) {
        const maxVolume = Math.max(...active.data.map(s => s.value_traded || s.volume || 0));
        active.data.forEach((stock, index) => {
          const ticker = NAME_TO_TICKER[stock.symbol] || stock.symbol;
          activeMap.set(ticker, {
            ...stock,
            symbol: ticker,
            originalName: stock.symbol,
            activityRank: index + 1,
            activityScore: maxVolume > 0 ? ((stock.value_traded || stock.volume || 0) / maxVolume) * 100 : 0
          });
        });
      }

      // Process High Potential (Value Score)
      if (highPotential.success && highPotential.data) {
        const maxUpside = Math.max(...highPotential.data.map(s => s.upside_to_high_pct || 0));
        highPotential.data.forEach((stock, index) => {
          highPotentialMap.set(stock.symbol, {
            ...stock,
            valueRank: index + 1,
            valueScore: maxUpside > 0 ? ((stock.upside_to_high_pct || 0) / maxUpside) * 100 : 0,
            dropFromHigh: stock.drop_from_high_pct || 0,
            upsidePotential: stock.upside_to_high_pct || 0
          });
        });
      }

      // Store raw data for recalculation
      rawData.current = { topPicksMap, activeMap, highPotentialMap };

      calculateRankings(topPicksMap, activeMap, highPotentialMap, weights);
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error calculating rankings:', err);
      setError('Failed to load rankings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateRankings = useCallback((topPicksMap, activeMap, highPotentialMap, w) => {
    // Combine all unique symbols (now using tickers for active stocks too)
    const allSymbols = new Set([
      ...topPicksMap.keys(),
      ...activeMap.keys(),
      ...highPotentialMap.keys()
    ]);

    const combinedRankings = [];

    allSymbols.forEach(symbol => {
      const topPick = topPicksMap.get(symbol);
      const activeStock = activeMap.get(symbol);
      const highPotentialStock = highPotentialMap.get(symbol);

      const appearances = [topPick, activeStock, highPotentialStock].filter(Boolean).length;
      
      const activityScore = activeStock?.activityScore || 0;
      const valueScore = highPotentialStock?.valueScore || 0;
      const analystScore = topPick?.analystScore || 0;

      // Calculate final score with current weights
      let finalScore = (
        (activityScore * w.activity) +
        (valueScore * w.value) +
        (analystScore * w.analyst)
      );

      // Overlap bonus: +10 for each additional list
      const overlapBonus = (appearances - 1) * 10;
      finalScore += overlapBonus;
      finalScore = Math.min(finalScore, 100);

      combinedRankings.push({
        symbol,
        name: topPick?.name || activeStock?.name || activeStock?.originalName || highPotentialStock?.name || symbol,
        price: topPick?.price || activeStock?.price || highPotentialStock?.price || 0,
        activityScore: Math.round(activityScore),
        valueScore: Math.round(valueScore),
        analystScore: Math.round(analystScore),
        finalScore: Math.round(finalScore * 10) / 10,
        appearances,
        inTopPicks: !!topPick,
        inActive: !!activeStock,
        inHighPotential: !!highPotentialStock,
        growthPotential: topPick?.growthPotential || 0,
        upsidePotential: highPotentialStock?.upsidePotential || 0,
        dropFromHigh: highPotentialStock?.dropFromHigh || 0,
        valueTraded: activeStock?.value_traded || 0
      });
    });

    // Sort by final score
    combinedRankings.sort((a, b) => b.finalScore - a.finalScore);
    combinedRankings.forEach((stock, index) => {
      stock.rank = index + 1;
    });

    setRankings(combinedRankings);
  }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const exportToCSV = () => {
    const headers = ['Rank', 'Symbol', 'Name', 'Activity Score', 'Value Score', 'Analyst Score', 'Final Score', 'Growth Potential %', 'Upside Potential %', 'In Top Picks', 'In Active', 'In High Potential'];
    const rows = rankings.map(stock => [
      stock.rank,
      stock.symbol,
      stock.name,
      stock.activityScore,
      stock.valueScore,
      stock.analystScore,
      stock.finalScore,
      stock.growthPotential?.toFixed(1) || 0,
      stock.upsidePotential?.toFixed(1) || 0,
      stock.inTopPicks ? 'Yes' : 'No',
      stock.inActive ? 'Yes' : 'No',
      stock.inHighPotential ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TiwiStock_Rankings_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-yellow-500 text-black';
    if (rank === 2) return 'bg-gray-300 text-black';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-card-hover text-muted-foreground';
  };

  const getSector = (symbol) => {
    const sectors = {
      'Mining': ['BHP', 'RIO', 'FMG', 'MIN', 'S32', 'NCM', 'NST', 'EVN', 'IGO', 'PLS', 'LTR', 'WDS', 'STO', 'ORG', 'WHC', 'YAL', 'SFR', 'NEM', 'PDN', 'RMS', 'PRU', 'WGX', 'SGM'],
      'Finance': ['CBA', 'NAB', 'WBC', 'ANZ', 'MQG', 'SUN', 'QBE', 'IAG', 'AMP', 'BEN', 'BOQ', 'HUB', 'IFL', 'NWL', 'ASX'],
      'Tech': ['XRO', 'WTC', 'CPU', 'APX', 'ALU', 'MP1', 'NXT', 'TNE', 'PME', 'TYR', 'REA', 'CAR', 'SEK', 'DRO'],
      'Healthcare': ['CSL', 'RMD', 'COH', 'SHL', 'PME', 'FPH', 'ANN', 'NAN', 'TLX', 'IMU', 'SIG'],
      'Retail': ['WOW', 'WES', 'COL', 'JBH', 'HVN', 'SUL', 'PMV', 'LOV', 'BRG', 'MYR', 'KGN', 'MTS'],
      'Property': ['GMG', 'SCG', 'VCX', 'GPT', 'MGR', 'CHC', 'DXS', 'BWP', 'CLW', 'SGP'],
      'Energy': ['WDS', 'STO', 'ORG', 'BPT', 'WHC', 'NHC', 'YAL', 'KAR', 'VEA', 'ALD'],
      'Telecom': ['TLS', 'TPG', 'VTL', 'SPK'],
      'FinTech': ['ZIP', 'APT', 'SQ2', 'TYR', 'HUM', 'SPT', 'OPY'],
      'Industrial': ['BXB', 'TCL', 'QAN', 'QUB', 'BSL', 'JHX', 'LNW', 'SOL', 'TWE']
    };
    
    for (const [sector, symbols] of Object.entries(sectors)) {
      if (symbols.includes(symbol)) return sector;
    }
    return null;
  };

  const getSectorColor = (sector) => {
    const colors = {
      'Mining': 'bg-amber-500/20 text-amber-400',
      'Finance': 'bg-green-500/20 text-green-400',
      'Tech': 'bg-blue-500/20 text-blue-400',
      'Healthcare': 'bg-pink-500/20 text-pink-400',
      'Retail': 'bg-purple-500/20 text-purple-400',
      'Property': 'bg-cyan-500/20 text-cyan-400',
      'Energy': 'bg-orange-500/20 text-orange-400',
      'Telecom': 'bg-indigo-500/20 text-indigo-400',
      'FinTech': 'bg-violet-500/20 text-violet-400',
      'Industrial': 'bg-teal-500/20 text-teal-400'
    };
    return colors[sector] || 'bg-gray-500/20 text-gray-400';
  };

  const getAppearanceBadges = (stock) => {
    const badges = [];
    if (stock.inTopPicks) badges.push({ label: 'Analyst Pick', color: 'bg-purple-500/20 text-purple-400' });
    if (stock.inActive) badges.push({ label: 'Most Active', color: 'bg-blue-500/20 text-blue-400' });
    if (stock.inHighPotential) badges.push({ label: 'High Potential', color: 'bg-green-500/20 text-green-400' });
    return badges;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Calculating smart rankings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-gradient-to-r from-yellow-900/20 to-background border-b border-yellow-500/20 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold font-display flex items-center gap-2">
                <Trophy className="w-7 h-7 text-yellow-500" />
                Smart Rankings
              </h1>
              <p className="text-sm text-muted-foreground">Combined analysis from all data sources</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={exportToCSV}
                className="p-2 rounded-full bg-card-hover hover:bg-green-500/20 transition-colors"
                title="Export to CSV"
                data-testid="export-csv-btn"
              >
                <Download className="w-5 h-5 text-green-400" />
              </button>
              <button 
                onClick={fetchData}
                className="p-2 rounded-full bg-card-hover hover:bg-primary/20 transition-colors"
                title="Refresh Rankings"
                data-testid="refresh-rankings-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Weight Controls */}
          <div className="mt-4 p-4 bg-card rounded-lg border border-border">
            <h3 className="text-sm font-semibold mb-3">Adjust Weights</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Activity ({Math.round(weights.activity * 100)}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={weights.activity * 100}
                  data-testid="weight-activity-slider"
                  onChange={(e) => {
                    const newActivity = parseInt(e.target.value) / 100;
                    const remaining = 1 - newActivity;
                    const ratio = weights.value / (weights.value + weights.analyst) || 0.5;
                    setWeights({
                      activity: newActivity,
                      value: remaining * ratio,
                      analyst: remaining * (1 - ratio)
                    });
                  }}
                  className="w-full h-2 bg-blue-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Value ({Math.round(weights.value * 100)}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={weights.value * 100}
                  data-testid="weight-value-slider"
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) / 100;
                    const remaining = 1 - newValue;
                    const ratio = weights.activity / (weights.activity + weights.analyst) || 0.5;
                    setWeights({
                      activity: remaining * ratio,
                      value: newValue,
                      analyst: remaining * (1 - ratio)
                    });
                  }}
                  className="w-full h-2 bg-green-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Target className="w-3 h-3" /> Analyst ({Math.round(weights.analyst * 100)}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={weights.analyst * 100}
                  data-testid="weight-analyst-slider"
                  onChange={(e) => {
                    const newAnalyst = parseInt(e.target.value) / 100;
                    const remaining = 1 - newAnalyst;
                    const ratio = weights.activity / (weights.activity + weights.value) || 0.5;
                    setWeights({
                      activity: remaining * ratio,
                      value: remaining * (1 - ratio),
                      analyst: newAnalyst
                    });
                  }}
                  className="w-full h-2 bg-purple-900 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Formula: Final = (Activity x {weights.activity.toFixed(1)}) + (Value x {weights.value.toFixed(1)}) + (Analyst x {weights.analyst.toFixed(1)}) + Overlap Bonus
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span>{rankings.length} stocks analyzed</span>
            <span>*</span>
            <span>{rankings.filter(s => s.appearances >= 2).length} appear in multiple lists</span>
            {lastUpdated && (
              <>
                <span>*</span>
                <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
            <p className="text-red-500">{error}</p>
            <button 
              onClick={fetchData}
              className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              data-testid="retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Rankings List */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Search bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search ticker or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-emerald-500/50"
              data-testid="rankings-search"
            />
          </div>
        </div>
        <div className="max-w-7xl mx-auto space-y-3">
          {rankings
            .filter(stock => {
              if (!search) return true;
              const q = search.toLowerCase();
              return (stock.symbol || '').toLowerCase().includes(q) || (stock.name || '').toLowerCase().includes(q);
            })
            .map((stock) => (
            <div
              key={stock.symbol}
              className="stock-card"
              data-testid={`ranking-${stock.symbol}`}
            >
              <div className="flex items-center gap-4">
                {/* Rank Badge */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getRankBadge(stock.rank)}`}>
                  {stock.rank}
                </div>

                {/* Stock Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold">{stock.symbol}</h3>
                    {getSector(stock.symbol) && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getSectorColor(getSector(stock.symbol))}`} data-testid={`sector-tag-${stock.symbol}`}>
                        {getSector(stock.symbol)}
                      </span>
                    )}
                    {stock.appearances >= 2 && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full" data-testid={`overlap-badge-${stock.symbol}`}>
                        +{(stock.appearances - 1) * 10} overlap bonus
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{stock.name}</p>
                  
                  {/* Appearance Badges */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {getAppearanceBadges(stock).map((badge, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.label}
                      </span>
                    ))}
                  </div>

                  {/* Score Breakdown */}
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <Activity className="w-3 h-3 text-blue-400" />
                      <span className="text-muted-foreground">Activity:</span>
                      <span className={getScoreColor(stock.activityScore)}>{stock.activityScore}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-green-400" />
                      <span className="text-muted-foreground">Value:</span>
                      <span className={getScoreColor(stock.valueScore)}>{stock.valueScore}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Target className="w-3 h-3 text-purple-400" />
                      <span className="text-muted-foreground">Analyst:</span>
                      <span className={getScoreColor(stock.analystScore)}>{stock.analystScore}</span>
                    </div>
                  </div>
                </div>

                {/* Final Score */}
                <div className="text-right">
                  <div className={`text-3xl font-bold ${getScoreColor(stock.finalScore)}`}>
                    {stock.finalScore}
                  </div>
                  <div className="text-xs text-muted-foreground">Final Score</div>
                  {stock.price > 0 && (
                    <div className="text-sm text-muted-foreground mt-1">
                      ${stock.price.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Extra Details */}
              {(stock.growthPotential > 0 || stock.upsidePotential > 0) && (
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-6 text-sm">
                  {stock.growthPotential > 0 && (
                    <div>
                      <span className="text-muted-foreground">Analyst Target:</span>
                      <span className="ml-1 text-green-400">+{stock.growthPotential.toFixed(1)}%</span>
                    </div>
                  )}
                  {stock.upsidePotential > 0 && (
                    <div>
                      <span className="text-muted-foreground">Upside to High:</span>
                      <span className="ml-1 text-green-400">+{stock.upsidePotential.toFixed(1)}%</span>
                    </div>
                  )}
                  {stock.dropFromHigh > 0 && (
                    <div>
                      <span className="text-muted-foreground">Down from High:</span>
                      <span className="ml-1 text-red-400">-{stock.dropFromHigh.toFixed(1)}%</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SmartRankingsPage;
